import React from 'react';
import { MapPin } from 'lucide-react';

const formatNumber = (num) => {
  if (!num) return '0';
  return num.toLocaleString();
};

const CSPRealTimeTab = ({ realTimeData }) => {
  if (!realTimeData) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-blue-100 text-sm">Active Devices</p>
          <p className="text-3xl font-bold">{formatNumber(realTimeData.summary?.active_devices)}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-green-100 text-sm">Active Sessions</p>
          <p className="text-3xl font-bold">{formatNumber(realTimeData.summary?.active_sessions)}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-purple-100 text-sm">Throughput</p>
          <p className="text-3xl font-bold">{realTimeData.summary?.throughput_gbps} Gbps</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <p className="text-orange-100 text-sm">Packets/sec</p>
          <p className="text-3xl font-bold">{formatNumber(realTimeData.summary?.packets_per_second)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Traffic by Protocol</h3>
          <div className="space-y-3">
            {Object.entries(realTimeData.traffic_by_protocol || {}).map(([protocol, data]) => (
              <div key={protocol}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="uppercase font-medium">{protocol}</span>
                  <span>{data.percent}% ({data.bytes_gb} GB)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className={`h-2 rounded-full ${protocol === 'tcp' ? 'bg-blue-500' : protocol === 'udp' ? 'bg-green-500' : protocol === 'icmp' ? 'bg-yellow-500' : 'bg-gray-400'}`} style={{ width: `${data.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Top Ports</h3>
          <div className="space-y-3">
            {realTimeData.top_ports?.map((port) => (
              <div key={`${port.port}-${port.protocol}`} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">{port.port}</span>
                  <span className="text-sm font-medium">{port.protocol}</span>
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{formatNumber(port.connections)} conn</p>
                  <p className="text-gray-500">{port.bytes_gb} GB</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Geographic Distribution</h3>
          <div className="space-y-3">
            {realTimeData.geographic_distribution?.map((region) => (
              <div key={region.region} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">{region.region}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">{formatNumber(region.devices)} devices</span>
                  <span className="text-sm font-medium w-12 text-right">{region.traffic_percent}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Device Types</h3>
          <div className="space-y-3">
            {realTimeData.device_types?.map((device) => (
              <div key={device.type} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                <span className="text-sm font-medium">{device.type}</span>
                <div className="text-right text-sm">
                  <p>{formatNumber(device.count)} devices</p>
                  <p className="text-gray-500">{device.data_gb} GB</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSPRealTimeTab;
